import { numberFormat } from '@hotjar/insights-common/lib/number';
import type { HeatmapHighlight } from '@hotjar/webapp/domains/highlights/model';

import { triggerDownload } from '../trigger-download';

const MIN_WIDTH = 500;

function getDownloadName(id: string, description: string | null): string {
  const name = [`Hotjar Highlight ${id}`, description].filter(Boolean).join(' - ');
  return `${name}.png`;
}

function drawStats(
  ctx: CanvasRenderingContext2D,
  options: {
    highlight: HeatmapHighlight;
    eventCopy: string;
    maxWidth: number;
  },
) {
  const { highlight, eventCopy, maxWidth } = options;
  const clicksCount = highlight.metadata.clicksCount ?? 0;
  const clicksPercentage = highlight.metadata.clicksPercentage ?? 0;

  if (clicksCount === 0 || clicksPercentage === 0) {
    return;
  }

  const statsText = `${numberFormat(clicksCount)} ${eventCopy} (${numberFormat(
    clicksPercentage,
  )}%)`;

  ctx.font = '500 14px Roboto, Arial, sans-serif';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
  ctx.textBaseline = 'top';

  const metrics = ctx.measureText(statsText);

  const padding = 20;
  const x = 0;
  const y = 0;

  ctx.fillRect(
    x,
    y,
    metrics.width + padding,
    metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent + 10,
  );

  ctx.fillStyle = 'rgba(0, 0, 0, 0.89)';
  ctx.fillText(statsText, x + padding / 2, y + padding / 3, maxWidth);
}

interface DownloadProps {
  highlight: HeatmapHighlight;
  svg: SVGSVGElement;
  eventCopy: string;
}

export function downloadHeatmapHighlight(options: DownloadProps): Promise<string> {
  const { svg, highlight, eventCopy } = options;
  const { selectedArea } = highlight.metadata;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (ctx === null) {
    return Promise.reject(new Error('Canvas context is null'));
  }

  return new Promise((resolve, reject) => {
    // Convert the svg to a string so we can create a blob for it.
    const data = new XMLSerializer().serializeToString(svg);

    const blob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);

    const img = new Image();

    img.onload = () => {
      const aspectRatio = img.naturalWidth / img.naturalHeight;

      canvas.width = Math.max(MIN_WIDTH, selectedArea.x2 - selectedArea.x1);
      canvas.height = canvas.width / aspectRatio;

      ctx.drawImage(img, 0, 0);

      drawStats(ctx, {
        eventCopy,
        highlight,
        maxWidth: canvas.width,
      });

      const canvasImg = canvas.toDataURL('image/png');

      const imgURI = canvasImg.replace('image/png', 'image/octet-stream');

      triggerDownload(imgURI, getDownloadName(highlight.id, highlight.description));

      window.URL.revokeObjectURL(url);

      resolve(imgURI);
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${url}`));
    };

    img.src = url;
  });
}
